import { useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { AdminCancelAwaitingResultDto } from "../models/responses/AdminCancelAwaitingResultDto";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { getActAsOrgId } from "../../contexts/actAsStore";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { PagedResponse } from "../models/responses/pagedResponse";
import {
  DeliveryType,
  LogoPrice,
  Order,
  OrderDeliveryPreferences,
  OrderStatus,
  Packaging,
} from "../../core/models/entities/Order";
import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
  CartSummary,
} from "../../presentation/screens/order-create/models/CartItem";
import { mapToCreateOrder } from "../models/mappers/mapCartOrders";
import {
  mapLogoPrice,
  mapToCreateOrderDeliveryPreferences,
  mapToOrder,
} from "../models/mappers/mapOrder";
import { Verdict } from "../../presentation/components/ApprovalVoting/approval-voting";
import {
  DesignTemplate,
  LogoTemplate,
} from "../../core/models/entities/DesignTemplate";
import {
  dissmissToast,
  useLoadingToast,
} from "../../presentation/components/Toast/Toast";
import { OrderDto } from "../models/responses/OrderDto";
import { PlaceOrderAckDto } from "../models/responses/PlaceOrderAckDto";
import { PrepaymentStatus } from "../../core/models/entities/PrepaymentStatus";
import { mapToPrepaymentStatus } from "../models/mappers/mapPrepaymentStatus";

export interface OrderRepository {
  // Admin-only cancel of an "Awaiting payment" order (race-hardened endpoint,
  // API flag AdminOrderCancelEnabled). Invalidates the ["orders"] queries.
  cancelAwaitingOrder(orderId: number): Promise<AdminCancelAwaitingResultDto>;
  useAllOrders(
    query: PageQuery,
    enabled?: boolean,
    options?: { keepPreviousData?: boolean }
  ): Resource<PagedResponse<Order>>;
  prefetchOrders(query: PageQuery): Promise<void>;
  usePendingOrdersCount(enabled: boolean): UseQueryResult<{ count: number }>;
  useAwaitingPaymentSummary(enabled: boolean): UseQueryResult<{
    count: number;
    byCurrency: { currency: string; count: number; total: number }[];
  }>;
  useOrder(orgId: number, orderId: number, lightweight?: boolean): Resource<Order>;
  useOrderByNumber(
    orderNumber: string | null,
    options?: { keepPollingWhileAwaitingPayment?: boolean }
  ): Resource<Order | undefined>;
  usePrepaymentStatus(
    orgId: number | undefined,
    enabled: boolean
  ): Resource<PrepaymentStatus>;
  createOrder(orgId: number, orderlines: CartItem[]): Promise<Order>;
  commitOrder(
    orgId: number,
    orderId: number,
    orderDeliveryPreferences: OrderDeliveryPreferences,
    orderlines?: CartItem[]
  ): Promise<Order>;
  placeOrder(
    orgId: number,
    orderDeliveryPreferences: OrderDeliveryPreferences,
    orderlines?: CartItem[],
    onBehalfOfUserId?: number
  ): Promise<PlaceOrderAckDto>;
  summarizeOrder(orgId: number, items: CartItem[]): Promise<CartSummary>;
  placeVerdict(order: Order, verdict: Verdict): Promise<Order>;
  setPackagingAndDelivery(
    order: Order,
    delieryType: DeliveryType,
    packaging: Packaging
  ): Promise<OrderDto>;
  downloadTemplate(
    orgId: number,
    designsItems: CartItemDesign[]
  ): Promise<Blob>;
  uploadDesignTemplate(
    orgId: number,
    spreadsheet: File
  ): Promise<DesignTemplate>;
  uploadLogoTemplate(orgId: number, spreadsheet: File): Promise<LogoTemplate>;
  reorder(orgId: number, orderId: number): Promise<Order>;
  updateOrder(
    orgId: number,
    orderId: number,
    orderlines: CartItem[]
  ): Promise<Order>;
}

export function useOrderRepository(): OrderRepository {
  const apiClient = useApiClient();

  // Act-as-vendor: ApiClient appends actAsOrgId to the list/badge endpoints,
  // so responses differ per impersonation scope — the scope MUST be in the
  // cache key, or the admin's unscoped rows are served from cache after
  // entering act-as (and vice versa after exit). 0 = not impersonating.
  // Read per call (not memoized): hooks re-run on the profile/role re-render
  // that every act-as switch triggers, picking up the new scope.
  const actAsKey = () => getActAsOrgId() ?? 0;

  const queryClient = useQueryClient();
  async function invalidateQueries() {
    // Invalidate instead of the old refetchQueries({type:'all', refetchPage:
    // () => true}): a mutation used to fire an immediate refetch of EVERY
    // cached ["orders"] query (all list pages, badge, detail views), most of
    // them not even on screen. Now only active (mounted) queries refetch;
    // inactive ones are just marked stale and refetch when their screen is
    // next shown (useAllOrders/useOrder set refetchOnMount: true, because the
    // global default refetchOnMount: false in index.tsx would otherwise never
    // refetch an invalidated inactive query on remount).
    await queryClient.invalidateQueries({
      queryKey: ["orders"],
      refetchType: "active",
    });
  }

  // Lightweight header-badge count: a pure SQL COUNT on the API instead of
  // fetching up to 1000 full orders. Key starts with "orders" so order
  // mutations invalidate it along with the lists.
  function usePendingOrdersCount(enabled: boolean) {
    return useQuery(
      ["orders", "pending-count", actAsKey()],
      () => apiClient.getPendingOrdersCount(),
      {
        refetchInterval: 600000,
        refetchOnMount: true,
        enabled,
      }
    );
  }

  // Stripe prepayment: outstanding "Awaiting payment" summary for the order
  // list header. Key starts with "orders" so order mutations (place, cancel,
  // verdict) invalidate it along with the lists — a payment landing via the
  // webhook is picked up by the 10-minute interval or the next list visit.
  function useAwaitingPaymentSummary(enabled: boolean) {
    return useQuery(
      ["orders", "awaiting-payment-summary", actAsKey()],
      () => apiClient.getAwaitingPaymentSummary(),
      {
        refetchInterval: 600000,
        refetchOnMount: true,
        enabled,
      }
    );
  }

  function useAllOrders(
    query: PageQuery,
    enabled: boolean = true,
    options?: { keepPreviousData?: boolean }
  ): Resource<PagedResponse<Order>> {
    return useQuery(
      ["orders", actAsKey(), toQueryParam(query)],
      () => apiClient.getAllOrders(query),
      {
        select: (data) => ({ ...data, results: data.results.map(mapToOrder) }),
        refetchInterval: 600000, // Refetch every 10 minutes (600,000 ms)
        enabled,
        // Required companion of the invalidate-active mutation strategy above:
        // a list page that was invalidated while unmounted must refetch when
        // the user returns to it (the global refetchOnMount: false would keep
        // showing the stale rows forever).
        refetchOnMount: true,
        // keepPreviousData is OPT-IN (default off), same pattern as
        // LogoRepository.useAllLogos: with it, isLoading stays false on query
        // changes, which changes what consumers observe. Only the order-list
        // screen opts in (it dims previous rows via isFetching/isPreviousData);
        // the nav-badge instance and any other consumer keep today's behavior
        // (the commission-editor regression came from making this a default).
        keepPreviousData: options?.keepPreviousData ?? false,
      }
    );
  }

  // Cache-warming twin of useAllOrders (same key and queryFn, default
  // options) for the paginator hover-prefetch and the /order login wave.
  // Fail-silent: prefetchQuery never throws, and on a cache miss useAllOrders
  // fetches exactly as it does today.
  function prefetchOrders(query: PageQuery): Promise<void> {
    return queryClient.prefetchQuery(
      ["orders", actAsKey(), toQueryParam(query)],
      () => apiClient.getAllOrders(query)
    );
  }

  // Look an order up in any cached order-list page. The raw list DTO is a
  // valid (partial) OrderDto: header fields (id, orderNumber, status, dates,
  // totals, organization, approval, delivery address) match what GetOrder
  // returns, while lines carry only the product info (no design/logo objects,
  // no images, no displayNo) - the detail screens render those gaps as empty
  // image/type cells until the real fetch lands. Detail queries share the
  // ["orders"] prefix but hold a plain OrderDto (no .results), so filter to
  // paged shapes.
  function findOrderInListCaches(orderId: number): OrderDto | undefined {
    const entries = queryClient.getQueriesData<PagedResponse<OrderDto>>({
      queryKey: ["orders"],
    });
    for (const [, data] of entries) {
      const results = data?.results;
      if (!Array.isArray(results)) continue;
      const match = results.find((o) => o?.id === orderId);
      // Skip rows without delivery/packaging: OrderConfirmationComponent has
      // a mount-only effect that fires a real setPackagingAndDelivery
      // mutation when order.deliveryType == null. That decision must never be
      // taken on a possibly-stale list row - for those (rare, legacy) orders
      // we keep today's behavior and wait for the real fetch.
      if (match && match.deliveryType != null && match.packagingType != null) {
        return match;
      }
    }
    return undefined;
  }

  function useOrder(
    orgId: number,
    orderId: number,
    lightweight?: boolean
  ): Resource<Order> {
    return useQuery(
      ["orders", orderId, lightweight ? "light" : "full"],
      () => apiClient.getOrder(orgId, orderId, lightweight),
      {
        select: (data) => mapToOrder(data),
        // See invalidateQueries: detail views invalidated by a mutation while
        // unmounted must refetch on the next visit.
        refetchOnMount: true,
        // placeholderData (NOT initialData): the real GetOrder fetch always
        // runs; the list row is only shown while it is in flight, so the
        // header (order no/status/customer/dates/prices) paints instantly
        // when arriving from the order list. Never persisted to the cache.
        placeholderData: () => findOrderInListCaches(orderId),
      }
    );
  }

  // Look a just-placed order up by its order number (the only handle
  // PlaceOrder returns - placement itself runs on a queue, so the order id
  // is unknown and the order may not even be committed yet when /order/end
  // renders). Polls the list endpoint until the order has left the transient
  // states, so the order-placed screen can surface a Stripe "Awaiting
  // payment" divert immediately without a manual refresh. Polling stops on
  // unmount (react-query) and once a settled status arrives.
  //
  // keepPollingWhileAwaitingPayment: on a Stripe Checkout return landing
  // (?stripe=success|cancelled) "Awaiting payment" is NOT a terminal state —
  // success races the webhook that moves the order onwards, and after a
  // cancel a retried payment (Pay now opens a new tab) settles the order
  // while the old tab is still open. refetchOnWindowFocus is globally off,
  // so without continued polling those screens would never see the flip.
  function useOrderByNumber(
    orderNumber: string | null,
    options?: { keepPollingWhileAwaitingPayment?: boolean }
  ): Resource<Order | undefined> {
    return useQuery(
      ["orders", "by-number", actAsKey(), orderNumber],
      () =>
        apiClient.getAllOrders({
          searchTerm: orderNumber!,
          filterBy: [],
          sortBy: { field: "" },
          page: { size: 10, index: 1 },
        } as PageQuery),
      {
        enabled: !!orderNumber,
        select: (data) => {
          const dto = data.results?.find(
            (o) => o.orderNumber === orderNumber
          );
          return dto ? mapToOrder(dto) : undefined;
        },
        refetchInterval: (order) =>
          !order ||
          order.status === OrderStatus.Draft ||
          order.status === OrderStatus.Processing ||
          order.status === OrderStatus.Unknown ||
          (options?.keepPollingWhileAwaitingPayment === true &&
            order.status === OrderStatus.AwaitingPayment)
            ? 3000
            : false,
        refetchOnMount: true,
      }
    );
  }

  // Prepayment pre-check for the place-order flow. Prefetched when the
  // confirmation screen mounts (enabled + orgId known), so the answer sits in
  // the cache by the time the user clicks "Place order" and the dialog can pop
  // instantly. Deliberately fail-soft: retry is off, so any failure (incl. the
  // 404 while the endpoint isn't live yet, or a network error) settles quickly
  // into isError with data === undefined. The caller reads ONLY
  // data?.prepaymentRequired === true, so error/undefined both fall through to
  // a normal placement. A failing pre-check must NEVER block an order.
  function usePrepaymentStatus(
    orgId: number | undefined,
    enabled: boolean
  ): Resource<PrepaymentStatus> {
    return useQuery(
      ["prepayment-status", orgId],
      () => apiClient.getPrepaymentStatus(orgId!).then(mapToPrepaymentStatus),
      {
        enabled: enabled && orgId != null,
        // The requirement rarely flips mid-session; a short cache avoids a
        // refetch between mounting the screen and clicking confirm.
        staleTime: 60000,
        // API not guaranteed live yet — don't hammer it on 404/network error.
        retry: false,
        refetchOnWindowFocus: false,
      }
    );
  }

  async function createOrder(
    orgId: number,
    orderlines: CartItem[]
  ): Promise<Order> {
    const createOrderDto = mapToCreateOrder(orderlines);
    return await apiClient.createOrder(orgId, createOrderDto).then((res) => {
      invalidateQueries().then();
      return mapToOrder(res);
    });
  }

  const summarizeOrder = (orgId: number, items: CartItem[]) => {
    const request = {
      products: items.map((item) => {
        const logoItem: CartItemLogo = item as CartItemLogo;
        const designItem: CartItemDesign = item as CartItemDesign;
        const id =
          logoItem?.baseLogo?.product?.sku ??
          designItem?.design?.product?.mul ??
          "";

        const hasCustomizableQuantity = ["NumberLogo", "NameLogo"].includes(
          logoItem?.baseLogo?.logoType
        );

        if (hasCustomizableQuantity) {
          const deliveryTotal = logoItem?.deliveryQuantities?.reduce(
            (total, q) => q.quantity + total,
            0
          );
          const storageTotal = logoItem?.storageQuantities?.reduce(
            (total, q) => q.quantity + total,
            0
          );

          const product = {
            productNo: id,
            toDelivery: deliveryTotal,
            toStorage: storageTotal,
          };
          return product;
        } else {
          const deliveryQty =
            logoItem?.toDelivery ||
            designItem?.instances?.reduce(
              (q, i) => (i ? q + i.toDelivery : 0),
              0
            );

          const product = {
            productNo: id,
            toDelivery: deliveryQty,
            toStorage: logoItem?.toStorage ?? 0,
          };
          return product;
        }
      }),
    };
    return apiClient.summarizeOrder(orgId, request);
  };

  async function commitOrder(
    orgId: number,
    orderId: number,
    orderDeliveryPreferences: OrderDeliveryPreferences,
    orderlines?: CartItem[]
  ): Promise<Order> {
    const products = orderlines ? mapToCreateOrder(orderlines) : undefined;
    const createOrderDeliveryPreferencesDto =
      mapToCreateOrderDeliveryPreferences(
        orderDeliveryPreferences,
        products?.products
      );
    return await apiClient
      .commitOrder(orgId, orderId, createOrderDeliveryPreferencesDto)
      .then((res) => {
        invalidateQueries().then();
        return mapToOrder(res);
      });
  }

  // Returns the full placement ack: with synchronous Stripe Checkout enabled
  // server-side, a prepayment-diverted order carries a checkoutUrl the caller
  // must redirect to (instead of showing the receipt). Old servers return only
  // { orderNumber } — the optional fields are simply absent then.
  async function placeOrder(
    orgId: number,
    orderDeliveryPreferences: OrderDeliveryPreferences,
    orderlines?: CartItem[],
    onBehalfOfUserId?: number
  ): Promise<PlaceOrderAckDto> {
    const products = orderlines ? mapToCreateOrder(orderlines) : undefined;
    const dto = mapToCreateOrderDeliveryPreferences(
      orderDeliveryPreferences,
      products?.products,
      onBehalfOfUserId
    );
    const result = await apiClient.placeOrder(orgId, dto);
    invalidateQueries().then();
    return result;
  }

  async function placeVerdict(order: Order, verdict: Verdict): Promise<Order> {
    return await apiClient.placeVerdictOrder(order, verdict).then((res) => {
      invalidateQueries().then();
      return res;
    });
  }

  async function setPackagingAndDelivery(
    order: Order,
    deliveryType: DeliveryType,
    packaging: Packaging
  ): Promise<OrderDto> {
    return await apiClient
      .setPackagingAndDelivery(order, deliveryType, packaging)
      .then((res) => {
        invalidateQueries().then();
        return res;
      });
  }

  async function downloadTemplate(
    orgId: number,
    designs: CartItemDesign[]
  ): Promise<Blob> {
    const toast = useLoadingToast("Downloading design template");
    // No invalidation here: downloading a template is a pure read (it mutates
    // no order), so it must not trigger order refetches.
    return await apiClient
      .downloadTemplate(orgId, designs)
      .finally(() => dissmissToast(toast));
  }

  async function uploadDesignTemplate(
    orgId: number,
    spreadsheet: File
  ): Promise<DesignTemplate> {
    const toast = useLoadingToast("Uploading design template");
    return await apiClient
      .uploadDesignTemplate(orgId, spreadsheet)
      .then((res) => {
        return { ...res };
      })
      .finally(() => dissmissToast(toast));
  }

  async function uploadLogoTemplate(
    orgId: number,
    spreadsheet: File
  ): Promise<LogoTemplate> {
    const toast = useLoadingToast("Uploading design template");
    return await apiClient
      .uploadLogoTemplate(orgId, spreadsheet)
      .then((res) => {
        return { ...res };
      })
      .finally(() => dissmissToast(toast));
  }

  async function reorder(orgId: number, orderId: number): Promise<Order> {
    return await apiClient.reorder(orgId, orderId).then((res) => {
      invalidateQueries().then();
      return mapToOrder(res);
    });
  }

  async function updateOrder(
    orgId: number,
    orderId: number,
    orderlines: CartItem[]
  ): Promise<Order> {
    const updateOrderDto = mapToCreateOrder(orderlines);
    return await apiClient
      .updateOrder(orgId, orderId, updateOrderDto)
      .then((res) => {
        invalidateQueries().then();
        return mapToOrder(res);
      });
  }

  // Admin-only cancel of an "Awaiting payment" order. Invalidate AFTER the
  // API call: list, awaiting-payment banner and pending-count all share the
  // ["orders"] key prefix, so one invalidation refreshes them together (the
  // system-health repository's cancelStrandedOrder deliberately does NOT do
  // this - it only refreshes the health page).
  async function cancelAwaitingOrder(
    orderId: number,
  ): Promise<AdminCancelAwaitingResultDto> {
    const result = await apiClient.adminCancelAwaitingOrder(orderId);
    await invalidateQueries();
    return result;
  }

  return {
    useAllOrders,
    cancelAwaitingOrder,
    prefetchOrders,
    useOrder,
    useOrderByNumber,
    usePrepaymentStatus,
    createOrder,
    commitOrder,
    placeOrder,
    summarizeOrder,
    placeVerdict,
    setPackagingAndDelivery,
    downloadTemplate,
    usePendingOrdersCount,
    useAwaitingPaymentSummary,
    uploadDesignTemplate,
    uploadLogoTemplate,
    reorder,
    updateOrder,
  };
}
